import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "./api";

const AuthContext = createContext(null);

/**
 * Preview overlay — while active, usePerms uses this instead of the user's
 * real perms. Stored in sessionStorage so it survives HMR & route changes.
 */
const PREVIEW_KEY = "preview_perms_v1";
function loadPreview() {
  try {
    const raw = sessionStorage.getItem(PREVIEW_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, obj=user
  const [perms, setPerms] = useState(null); // {is_admin, permissions}
  const [preview, setPreview] = useState(loadPreview()); // {team_name, permissions} or null
  const [error, setError] = useState("");

  const fetchPerms = async () => {
    try {
      const { data } = await api.get("/auth/permissions");
      setPerms(data);
    } catch (_) {
      setPerms({ is_admin: false, permissions: {} });
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) { setUser(false); return; }
    api.get("/auth/me")
      .then((r) => { setUser(r.data); fetchPerms(); })
      .catch((err) => {
        // Only a rejected token signs the user out. A network error or 5xx
        // (e.g. backend restarting) keeps the token so a reload doesn't log
        // everyone out (ticket 291868).
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem("access_token");
          setUser(false);
          return;
        }
        setTimeout(() => {
          api.get("/auth/me")
            .then((r) => { setUser(r.data); fetchPerms(); })
            .catch((e2) => {
              const s2 = e2?.response?.status;
              if (s2 === 401 || s2 === 403) localStorage.removeItem("access_token");
              setUser(false);
            });
        }, 5000);
      });
  }, []);

  // Auto-refresh perms on window focus and every 60s while the tab is active,
  // so team-permission edits propagate without requiring re-login.
  useEffect(() => {
    if (!user) return;
    const onFocus = () => fetchPerms();
    window.addEventListener("focus", onFocus);
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchPerms();
    }, 60000);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(id);
    };
  }, [user]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("access_token", data.access_token);
      setUser(data.user);
      await fetchPerms();
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    localStorage.removeItem("access_token");
    sessionStorage.removeItem(PREVIEW_KEY);
    setUser(false);
    setPerms(null);
    setPreview(null);
  };

  // Start previewing the app as if the current user belonged only to `team`.
  // Admins retain the ability to exit preview (banner + exitPreview()).
  const startPreview = (team) => {
    if (!team) return;
    const payload = {
      team_name: team.name,
      team_id: team.id,
      permissions: team.permissions || {},
      started_at: Date.now(),
    };
    sessionStorage.setItem(PREVIEW_KEY, JSON.stringify(payload));
    setPreview(payload);
  };
  const exitPreview = () => {
    sessionStorage.removeItem(PREVIEW_KEY);
    setPreview(null);
  };

  const updatePreferences = async (prefs) => {
    try {
      const { data } = await api.patch("/auth/preferences", prefs);
      setUser(data);
      return data;
    } catch (e) {
      setError(formatApiError(e));
      throw e;
    }
  };

  return (
    <AuthContext.Provider value={{
      user, perms, preview,
      login, logout, error, setUser,
      refreshPerms: fetchPerms,
      startPreview, exitPreview,
      updatePreferences,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

/**
 * usePerms – returns canView/canEdit for a module or field.
 * If a preview is active, use the preview perms and treat isAdmin as false.
 * Otherwise: admins bypass all checks. For regular users, field-level entries
 * (if any) override the module-level defaults.
 */
export function usePerms() {
  const { perms, preview, user } = useAuth();
  const usingPreview = !!preview;
  const isAdmin = !usingPreview && !!perms?.is_admin;
  const map = usingPreview ? (preview.permissions || {}) : (perms?.permissions || {});

  // Feb 26 2026 — View is authoritative. A field/module with `view: false`
  // is hidden even if `edit: true`. Previous logic OR'd view||edit which
  // leaked hidden fields whenever Edit was granted alone.
  // Aug 5 2026 — a module whose own View box is unticked used to vanish
  // entirely, even when individual fields had been granted View. That made the
  // field-level matrix unusable: ticking 24 fields achieved nothing, because
  // this gate rejected before a single field was consulted.
  //
  // A field grant now opens the module. And in that case — module box unticked,
  // access earned purely through field grants — only the granted fields are
  // visible, which is the "show this team just these fields" behaviour asked
  // for. When the module box IS ticked, nothing changes: unlisted fields stay
  // visible as before, so no existing team loses anything.
  const anyFieldGrant = (mp, kind) =>
    Object.values((mp && mp.fields) || {}).some((fp) =>
      fp && (kind === "edit" ? (fp.view && fp.edit) : fp.view));

  const canView = (module, field = null) => {
    if (isAdmin) return true;
    const mp = map[module];
    if (!mp) return false;
    const moduleOpen = !!mp.view;
    if (!moduleOpen && !anyFieldGrant(mp, "view")) return false;
    if (field && mp.fields && field in mp.fields) {
      return !!mp.fields[field].view;
    }
    // Unlisted field, and the module itself was never granted — do not assume
    // visible, or the unticked fields would leak straight back in.
    if (!moduleOpen && field) return false;
    return true;
  };
  const canEdit = (module, field = null) => {
    if (isAdmin) return true;
    const mp = map[module];
    if (!mp) return false;
    // Cannot edit what you can't see.
    const moduleEditable = !!(mp.view && mp.edit);
    if (!moduleEditable && !anyFieldGrant(mp, "edit")) return false;
    if (field && mp.fields && field in mp.fields) {
      const fp = mp.fields[field];
      return !!(fp.view && fp.edit);
    }
    if (!moduleEditable && field) return false;
    return true;
  };
  // Feb 27 2026 — delete is a distinct permission axis. Requires the module
  // to be viewable AND editable AND explicitly delete-flagged. There's no
  // field-level delete concept (you can't delete "just a column").
  const canDelete = (module) => {
    if (isAdmin) return true;
    const mp = map[module];
    if (!mp) return false;
    return !!(mp.view && mp.edit && mp.delete);
  };
  // Feb 27 2026 — fourth axis: bulk_edit gates the Bulk Update bar on
  // module surfaces. Independent from delete — a user can be allowed to
  // mass-update statuses without being allowed to hard-delete rows.
  const canBulkEdit = (module) => {
    if (isAdmin) return true;
    const mp = map[module];
    if (!mp) return false;
    return !!(mp.view && mp.edit && mp.bulk_edit);
  };
  // Mar 1 2026 — feature toggles (Print Labels / Download Template /
  // Admin Backfills). Absent value = allowed (backwards-compat for existing
  // users). Explicit ``false`` fully hides the button. Admins bypass.
  const canFeature = (name) => {
    if (isAdmin) return true;
    if (usingPreview) return preview?.feature_permissions?.[name] !== false;
    const fp = user?.feature_permissions || {};
    return fp[name] !== false;
  };
  // Aug 31 2026 — strict/opt-in variant for danger-zone toggles (e.g.
  // Admin Backfills) where the default-permissive semantics of `canFeature`
  // (absent = allowed) would silently grant a bulk/destructive action to
  // every non-admin who was never explicitly given it. Requires an explicit
  // `true`; absent or `false` both deny. Admins still bypass.
  const canFeatureExplicit = (name) => {
    if (isAdmin) return true;
    if (usingPreview) return preview?.feature_permissions?.[name] === true;
    const fp = user?.feature_permissions || {};
    return fp[name] === true;
  };
  // Mar 1 2026 — user's warehouse allowlist (empty = no restriction).
  // Callers use this to narrow the top-bar Warehouse dropdown options.
  // Admins get `null` = show everything.
  const allowedWarehouses = () => {
    if (isAdmin) return null;
    const list = user?.warehouse_names || [];
    return list.length ? list : null;  // empty = no restriction on the frontend
  };
  return { isAdmin, canView, canEdit, canDelete, canBulkEdit, canFeature, canFeatureExplicit, allowedWarehouses, ready: !!perms, usingPreview };
}
